/**
 * Audit Logging Methods
 *
 * Core logging functionality for different types of audit events
 */

import { PermissionActionType, AuditEvent } from './types';
import { SecurityEvent } from '../../permission-integrity';
import { getCurrentTimestamp, getCurrentISOTimestamp } from '@/utils/date';

export interface LoggingContext {
  anonymizeData: boolean;
  sessionId: string;
  auditLevel: string;
  shouldLog: (_action: PermissionActionType, _result: string) => boolean;
  createAuditEvent: (_params: Partial<AuditEvent>) => AuditEvent;
  addEvent: (_event: AuditEvent) => void;
  sendToSiem: (_event: AuditEvent) => void;
  hashUserId: (_userId?: string) => string | undefined;
  hashResourceId: (_resourceId?: string) => string | undefined;
  getUserAgent: () => string;
  generateComplianceFlags: (_permission?: string, _result?: string) => string[];
  mapSecurityLevel: (_severity: SecurityEvent['severity']) => string;
}

/**
 * Log a permission check event
 */
export function logPermissionCheck(
  context: LoggingContext,
  userId: string | undefined,
  permission: string,
  resource?: string,
  resourceId?: string,
  result: 'SUCCESS' | 'FAILURE' | 'DENIED' = 'SUCCESS',
  reason?: string,
  metadata: Record<string, unknown> = {}
): void {
  if (!context.shouldLog('PERMISSION_CHECK', result)) {
    return;
  }

  const event = context.createAuditEvent({
    ...(userId !== undefined && { userId: context.anonymizeData ? context.hashUserId(userId) : userId }),
    action: 'PERMISSION_CHECK',
    ...(resource !== undefined && { resource }),
    ...(permission !== undefined && { permission }),
    result: result as 'SUCCESS' | 'FAILURE' | 'DENIED',
    metadata: {
      ...metadata,
      timestamp: getCurrentTimestamp(),
      auditLevel: context.auditLevel,
      source: 'permission-audit',
      securityLevel: result === 'DENIED' ? 'MEDIUM' : 'LOW',
      complianceFlags: context.generateComplianceFlags(permission, result),
      ...(resourceId && { resourceId: context.anonymizeData ? context.hashResourceId(resourceId) : resourceId }),
      ...(reason && { reason })
    }
  } as Partial<AuditEvent>);

  context.addEvent(event);

  // Send to SIEM if it's a security-relevant event
  if (result === 'DENIED' || result === 'FAILURE') {
    context.sendToSiem(event);
  }
}

/**
 * Log a security event from the integrity service
 */
export function logSecurityEvent(
  context: LoggingContext,
  securityEvent: SecurityEvent
): void {
  const event = context.createAuditEvent({
    ...(securityEvent.userId !== undefined && { userId: context.anonymizeData ? context.hashUserId(securityEvent.userId) : securityEvent.userId }),
    action: 'DATA_ACCESS',
    result: 'DENIED',
    metadata: {
      ...securityEvent.details,
      securityEventType: securityEvent.type,
      originalTimestamp: securityEvent.timestamp,
      reason: `Security violation: ${securityEvent.type}`,
      source: securityEvent.source,
      securityLevel: context.mapSecurityLevel(securityEvent.severity),
      complianceFlags: ['SECURITY_INCIDENT', 'IMMEDIATE_REVIEW']
    }
  } as Partial<AuditEvent>);

  context.addEvent(event);
  context.sendToSiem(event);
}

/**
 * Log access attempt (successful or failed)
 */
export function logAccessAttempt(
  context: LoggingContext,
  userId: string | undefined,
  resource: string,
  resourceId?: string,
  result: 'SUCCESS' | 'FAILURE' | 'DENIED' = 'SUCCESS',
  reason?: string,
  metadata: Record<string, unknown> = {}
): void {
  const event = context.createAuditEvent({
    ...(userId !== undefined && { userId: context.anonymizeData ? context.hashUserId(userId) : userId }),
    action: 'DATA_ACCESS',
    ...(resource !== undefined && { resource }),
    result: result as 'SUCCESS' | 'FAILURE' | 'DENIED',
    metadata: {
      ...metadata,
      userAgent: context.getUserAgent(),
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      source: 'access-control',
      securityLevel: result === 'DENIED' ? 'HIGH' : 'LOW',
      complianceFlags: result === 'DENIED' ? ['ACCESS_DENIED', 'REVIEW_REQUIRED'] : ['ACCESS_GRANTED'],
      ...(resourceId && { resourceId: context.anonymizeData ? context.hashResourceId(resourceId) : resourceId }),
      ...(reason && { reason })
    }
  } as Partial<AuditEvent>);

  context.addEvent(event);

  if (result === 'DENIED') {
    context.sendToSiem(event);
  }
}

/**
 * Log forced logout due to security violation
 */
export function logForcedLogout(
  context: LoggingContext,
  userId: string | undefined,
  reason: string,
  metadata: Record<string, unknown> = {}
): void {
  const event = context.createAuditEvent({
    ...(userId !== undefined && { userId: context.anonymizeData ? context.hashUserId(userId) : userId }),
    action: 'USER_LOGOUT',
    result: 'SUCCESS',
    metadata: {
      ...metadata,
      forcedLogoutTime: getCurrentISOTimestamp(),
      reason,
      source: 'security-enforcement',
      securityLevel: 'CRITICAL',
      complianceFlags: ['SECURITY_ACTION', 'USER_TERMINATED', 'IMMEDIATE_REVIEW']
    }
  } as Partial<AuditEvent>);

  context.addEvent(event);
  context.sendToSiem(event);
}